import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-cream">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm mb-8 inline-block">&larr; Back to Home</Link>

        <h1 className="font-serif text-4xl font-semibold text-secondary-800 mb-2">Privacy Policy</h1>
        <p className="text-secondary-500 text-sm mb-12">Last updated: February 2026</p>

        <div className="prose prose-lg max-w-none space-y-8">

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">1. Introduction</h2>
            <p className="text-secondary-600 leading-relaxed">
              The Family Legacy Bridge ("we", "us", or "our") is committed to protecting your personal information in accordance with the Protection of Personal Information Act, 2013 (POPIA) of South Africa. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our website and services.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">2. Responsible Party</h2>
            <p className="text-secondary-600 leading-relaxed">
              The responsible party for the processing of your personal information is:
            </p>
            <div className="bg-secondary-50 rounded-xl p-6 mt-4">
              <p className="text-secondary-700 font-medium">Amy Jaffe</p>
              <p className="text-secondary-600">The Family Legacy Bridge</p>
              <p className="text-secondary-600">Email: amyjaffe@outlook.com</p>
            </div>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">3. Information We Collect</h2>
            <p className="text-secondary-600 leading-relaxed mb-4">We may collect the following personal information:</p>
            <ul className="space-y-2 text-secondary-600">
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span><strong>Contact information:</strong> name, email address, and phone number when you reach out to us or schedule a consultation.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span><strong>Assessment responses:</strong> information provided through our Family Wealth Assessment, including family dynamics, values, and perspectives on wealth stewardship.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span><strong>Communication records:</strong> correspondence between you and The Family Legacy Bridge.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span><strong>Website usage data:</strong> non-personal information such as browser type, pages visited, and time spent on the site.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">4. Purpose of Processing</h2>
            <p className="text-secondary-600 leading-relaxed mb-4">We process your personal information for the following purposes:</p>
            <ul className="space-y-2 text-secondary-600">
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>To provide our family wealth coaching services and deliver assessment results.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>To communicate with you about our services and respond to your enquiries.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>To improve our website and services.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>To comply with legal obligations under South African law.</span>
              </li>
            </ul>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">5. Legal Basis for Processing</h2>
            <p className="text-secondary-600 leading-relaxed">
              In accordance with POPIA, we process your personal information based on one or more of the following grounds: your consent, the performance of a contract or agreement with you, compliance with a legal obligation, or our legitimate interest in providing and improving our services.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">6. Sharing of Information</h2>
            <p className="text-secondary-600 leading-relaxed">
              We do not sell, trade, or share your personal information with third parties, except where required by law or with your explicit consent. Your assessment responses and consultation details are treated as strictly confidential.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">7. Data Security</h2>
            <p className="text-secondary-600 leading-relaxed">
              We take reasonable measures to protect your personal information from unauthorised access, loss, misuse, or alteration. However, no method of electronic storage or transmission is completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">8. Retention of Information</h2>
            <p className="text-secondary-600 leading-relaxed">
              We retain your personal information only for as long as necessary to fulfil the purposes for which it was collected, or as required by applicable South African law. Once your information is no longer needed, it will be securely deleted or anonymised.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">9. Your Rights Under POPIA</h2>
            <p className="text-secondary-600 leading-relaxed mb-4">As a data subject under POPIA, you have the right to:</p>
            <ul className="space-y-2 text-secondary-600">
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Request access to the personal information we hold about you.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Request correction of inaccurate or incomplete personal information.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Request deletion of your personal information where it is no longer necessary.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Object to the processing of your personal information.</span>
              </li>
              <li className="flex items-start">
                <span className="text-primary-500 mr-2 mt-1">•</span>
                <span>Lodge a complaint with the Information Regulator of South Africa.</span>
              </li>
            </ul>
            <p className="text-secondary-600 leading-relaxed mt-4">
              To exercise any of these rights, please contact us at amyjaffe@outlook.com.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">10. Cookies</h2>
            <p className="text-secondary-600 leading-relaxed">
              Our website may use cookies to enhance your browsing experience. Cookies are small files stored on your device. You can control cookie settings through your browser. Disabling cookies may affect certain features of our website.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">11. Changes to This Policy</h2>
            <p className="text-secondary-600 leading-relaxed">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="font-serif text-2xl font-semibold text-secondary-800 mb-4">12. Contact</h2>
            <p className="text-secondary-600 leading-relaxed">
              If you have any questions about this Privacy Policy or how we handle your personal information, please contact:
            </p>
            <div className="bg-secondary-50 rounded-xl p-6 mt-4">
              <p className="text-secondary-700 font-medium">Amy Jaffe</p>
              <p className="text-secondary-600">The Family Legacy Bridge</p>
              <p className="text-secondary-600">Email: amyjaffe@outlook.com</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
